import React, { useRef } from "react";
import { observer } from "mobx-react-lite";
import { Drawer, Typography } from "antd";
import { ExportOutlined } from "@ant-design/icons";
import DocumentViewer from "../../documents/components/DocumentViewer";
import { useDocumentsStore } from "../../documents/stores/context";
import { WbDocument } from "../../workbench/types/WbTypes";

type Props = {
    document: WbDocument;
    onClose: () => void;
    onDownload: (packageId: string) => void;
};

const WorkbenchNewDocumentPreviewDrawer: React.FC<Props> = ({
    document,
    onClose,
    onDownload,
}) => {
    const { documentViewVisualStore } = useDocumentsStore();
    const closeButton = useRef<HTMLElement>(null);
    const fullViewUrl = `/document-viewer/${encodeURIComponent(
        document.packageId,
    )}?fileName=${encodeURIComponent(document.fileName || "document")}`;

    const handleIconKeyDown = (
        event: React.KeyboardEvent<HTMLElement>,
        callback: () => void,
    ) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            callback();
        }
    };

    const handleClose = () => {
        onClose();
        documentViewVisualStore.setShowError(false);
    };

    return (
        <Drawer
            autoFocus={true}
            afterOpenChange={(visible) => {
                if (visible) closeButton.current?.focus();
            }}
            className="workbench-document-preview-drawer"
            closable={false}
            destroyOnClose={true}
            keyboard={true}
            onClose={handleClose}
            open={true}
            placement="right"
            rootClassName="workbench-document-preview-drawer-root"
            title={
                <div className="workbench-document-preview-drawer-heading">
                    <div className="workbench-document-preview-drawer-title-copy">
                        <Typography.Title
                            className="workbench-document-preview-drawer-title"
                            level={2}
                        >
                            {document.fileName || "Document preview"}
                        </Typography.Title>
                    </div>
                </div>
            }
            extra={
                <div className="workbench-document-preview-drawer-actions">
                    <i
                        aria-label="Download document"
                        className="alpha-icon md download"
                        onClick={() => onDownload(document.packageId)}
                        onKeyDown={(event) =>
                            handleIconKeyDown(event, () =>
                                onDownload(document.packageId),
                            )
                        }
                        role="button"
                        tabIndex={0}
                    />
                    <a
                        aria-label="Open document in new tab"
                        className="workbench-document-preview-drawer-open-link"
                        href={fullViewUrl}
                        rel="noreferrer"
                        target="_blank"
                    >
                        <ExportOutlined />
                    </a>
                    <i
                        aria-label="Close document preview"
                        ref={closeButton}
                        className="alpha-icon xs cross"
                        onClick={handleClose}
                        onKeyDown={(event) =>
                            handleIconKeyDown(event, handleClose)
                        }
                        role="button"
                        tabIndex={0}
                    />
                </div>
            }
            width="34vw"
        >
            <div className="workbench-document-preview-drawer-body">
                <DocumentViewer
                    store={documentViewVisualStore}
                    path={document.packageId}
                />
            </div>
        </Drawer>
    );
};

export default observer(WorkbenchNewDocumentPreviewDrawer);
